import logoUrl from '../../assets/images/Senerfi-formato.png';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { getQualityPrintMeta } from './qualityPrintConfig';

export async function printHvActivo(asset, options = {}) {
  const esc = (v) => String(v ?? '').replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
  const autoPrint = options?.autoPrint !== false;
  const renderForPdf = options?.renderForPdf === true || autoPrint === false;
  const qualityMeta = await getQualityPrintMeta('hv_activos');

  const formatBogotaDateTime = (d) => {
    const dt = d instanceof Date ? d : new Date();
    const parts = new Intl.DateTimeFormat('es-CO', {
      timeZone: 'America/Bogota',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(dt);
    const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    return `${map.day}/${map.month}/${map.year} ${map.hour}:${map.minute}:${map.second}`;
  };

  const formatCode = String(options?.qualityCode || qualityMeta?.code || 'FT-TIC-053').trim();
  const formatVersion = String(options?.qualityVersion || qualityMeta?.version || '00').trim();
  const fixedFormatDate = String(options?.qualityDate || qualityMeta?.effective_date_display || '02/02/2026').trim();
  const impresionFecha = formatBogotaDateTime(new Date());

  const basePath = (process.env.PUBLIC_URL || '/').replace(/\/$/, '');
  const resolvedLogo = `${basePath}${logoUrl}`;
  let logoSrc = resolvedLogo;
  try {
    const resp = await fetch(resolvedLogo, { cache: 'no-store' });
    if (resp.ok) {
      const blob = await resp.blob();
      logoSrc = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    }
  } catch (_) {}

  const a = asset || {};
  const serial = String(a.serial || '').trim();
  const brand = String(a.brand || '').trim();
  const model = String(a.model || '').trim();
  const materialCode = String(a.material_code || '').trim();
  const assetType = String(a.asset_type || '').trim();
  const status = String(a.status || '').trim();
  const responsibleName = String(a.assigned_to_name || a.assigned_admin_name || '').trim();
  const responsibleDni = String(a.assigned_to_dni || '').trim();
  const responsibleCargo = String(a.funcionario_cargo || '').trim();
  const responsibleArea = String(a.dependencia_area || '').trim();

  const inspectionType = String(a.inspection_type || '').trim();
  const inspectionDate = String(a.inspection_date || '').trim();
  const inspectionObs = String(a.inspection_observation || '').trim();

  const assignDate = String(a.assignment_date || '').trim();
  const assignReason = String(a.assignment_reason || '').trim();

  const generalNotes = String(a.general_notes || '').trim();

  const parseLicenses = () => {
    const raw = a.software_licenses_json || a.software_licenses || null;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      } catch (_) {}
    }
    const one = {
      name: a.software_name || '',
      license: a.software_license || '',
      expiry: a.software_expiry || '',
      notes: a.software_notes || '',
    };
    if (String(one.name || '').trim() || String(one.license || '').trim() || String(one.expiry || '').trim() || String(one.notes || '').trim()) return [one];
    return [];
  };
  const licenses = parseLicenses();

  const parseDiag = () => {
    const raw = a.diagnostic_protocol_json || a.diagnostic_protocol || null;
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
      } catch (_) {}
    }
    return {};
  };
  const diag = parseDiag();

  const history = Array.isArray(options?.history) ? options.history : [];

  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = renderForPdf ? 'auto' : '0';
  frame.style.bottom = renderForPdf ? 'auto' : '0';
  frame.style.left = renderForPdf ? '-10000px' : '0';
  frame.style.top = renderForPdf ? '0' : '0';
  frame.style.width = renderForPdf ? '1200px' : '0';
  frame.style.height = renderForPdf ? '1700px' : '0';
  frame.style.border = '0';
  frame.style.visibility = renderForPdf ? 'hidden' : 'visible';
  frame.style.pointerEvents = 'none';
  frame.name = 'PRINT';
  document.body.appendChild(frame);
  const w = frame.contentWindow;

  const title = `Formato HV-ACTIVOS - ${serial || 'Activo'}`;
  w.document.open();
  w.document.write(`<!doctype html><html><head><meta charset="utf-8" /><title>${esc(title)}</title>
    <style>
      @page { size: 210mm 297mm; margin: 6mm; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      body{font-family: Arial, sans-serif; color:#000;}
      .sheet{width: 100%; max-width: 19.6cm; margin: 0 auto; padding: 2px; display:flex; flex-direction:column;}
      table{width:100%; border-collapse:collapse; table-layout: fixed;}
      tr{page-break-inside:avoid}
      td, th{border:1px solid #000; padding:3px 4px; font-size:8.8px; vertical-align:top; line-height:1.12}
      .header-title{font-weight:700; font-size:11px}
      .center{text-align:center}
      .right{text-align:right}
      .section{margin-top:2px}
      .section-title{font-weight:700; background:#f7f7f7}
      .logo{height:38px; max-width:100%; object-fit:contain; display:block; margin:0 auto}
      .logo-box{display:flex; align-items:center; justify-content:center; height:100%}
      .version{font-size:7.5px; color:#555}
      .muted{color:#444}
      .diag td, .diag th{font-size:8px; padding:2px 3px; line-height:1.08}
      .diag .q{font-size:7.8px}
      .diag .cat{vertical-align:middle; text-align:center}
    </style>
  </head><body>
    <div class="sheet">
      <table>
        <tr>
          <td rowspan="3" style="width:30%; text-align:center; vertical-align:middle">
            <div class="logo-box">
              <img src="${logoSrc}" alt="Senerfi" class="logo" />
            </div>
          </td>
          <td style="width:50%" class="center header-title">SOLUCIONES ENERGETICAS FORESTALES INTEGRALES</td>
          <td style="width:20%" class="center">
            <div class="header-title">CODIGO</div>
            <div>${esc(formatCode)}</div>
          </td>
        </tr>
        <tr>
          <td class="center" style="font-weight:700">TECNOLOGIA Y MONITOREO</td>
          <td class="center">
            <div class="header-title">VERSION</div>
            <div>${esc(formatVersion)}</div>
          </td>
        </tr>
        <tr>
          <td class="center">FORMATO HOJA DE VIDA ACTIVOS TECNOLOGICOS</td>
          <td class="center">
            <div class="header-title">FECHA</div>
            <div>${fixedFormatDate}</div>
            <div class="version">Impresión v ${impresionFecha}</div>
          </td>
        </tr>
      </table>

      <table class="section">
        <tr>
          <td style="width:50%; vertical-align:top; padding:8px">
            <strong>Activo:</strong> <span class="muted">${esc(assetType)}</span>
            ${materialCode ? `<br><strong>Código material:</strong> <span class="muted">${esc(materialCode)}</span>` : ''}
          </td>
          <td style="width:50%; vertical-align:top; padding:8px">
            <strong>Estado:</strong> <span class="muted">${esc(status)}</span>
            ${serial ? `<br><strong>Serial:</strong> <span class="muted">${esc(serial)}</span>` : ''}
          </td>
        </tr>
        <tr>
          <td colspan="2"><strong>Responsable actual:</strong> <span class="muted">${esc(responsibleName || '—')}</span></td>
        </tr>
        <tr>
          <td><strong>Cc:</strong> <span class="muted">${esc(responsibleDni || '')}</span></td>
          <td><strong>Cargo:</strong> <span class="muted">${esc(responsibleCargo || '')}</span></td>
        </tr>
        <tr>
          <td colspan="2"><strong>Área:</strong> <span class="muted">${esc(responsibleArea || '')}</span></td>
        </tr>
      </table>

      <table class="section">
        <tr><th colspan="6" class="section-title">1. EQUIPOS</th></tr>
        <tr>
          <th style="width:6%" class="center">ITEM</th>
          <th style="width:18%" class="center">TIPO</th>
          <th style="width:24%" class="center">SERIE</th>
          <th style="width:18%" class="center">MARCA</th>
          <th style="width:22%" class="center">MODELO</th>
          <th style="width:12%" class="center">ESTADO</th>
        </tr>
        <tr>
          <td class="center">1</td>
          <td>${esc(assetType)}</td>
          <td>${esc(serial)}</td>
          <td>${esc(brand)}</td>
          <td>${esc(model)}</td>
          <td class="center">${esc(status)}</td>
        </tr>
      </table>

      <table class="section">
        <tr><th colspan="4" class="section-title">2. LICENCIAS</th></tr>
        <tr>
          <th style="width:34%" class="center">SOFTWARE</th>
          <th style="width:26%" class="center">LICENCIA / CLAVE</th>
          <th style="width:16%" class="center">VENCIMIENTO</th>
          <th style="width:24%" class="center">OBSERVACIONES</th>
        </tr>
        ${(licenses.length ? licenses : [{ name: '', license: '', expiry: '', notes: '' }]).slice(0, 3).map((l) => `
          <tr>
            <td>${esc(l?.name || l?.software_name || '')}</td>
            <td>${esc(l?.license || l?.software_license || '')}</td>
            <td class="center">${esc(l?.expiry || l?.software_expiry || '')}</td>
            <td>${esc(l?.notes || l?.software_notes || '')}</td>
          </tr>
        `).join('')}
      </table>

      <table class="section">
        <tr><th colspan="4" class="section-title">3. INSPECCION</th></tr>
        <tr>
          <td style="width:25%"><strong>Tipo</strong><br>${esc(inspectionType)}</td>
          <td style="width:25%"><strong>Fecha</strong><br>${esc(inspectionDate)}</td>
          <td colspan="2"><strong>Observación</strong><br>${esc(inspectionObs)}</td>
        </tr>
      </table>

      <table class="section">
        <tr><th colspan="4" class="section-title">4. ASIGNACION DE RESPONSABLE</th></tr>
        <tr>
          <td style="width:40%"><strong>Responsable</strong><br>${esc(responsibleName)}</td>
          <td style="width:20%"><strong>Cc</strong><br>${esc(responsibleDni)}</td>
          <td style="width:20%"><strong>Fecha</strong><br>${esc(assignDate)}</td>
          <td style="width:20%"><strong>Motivo</strong><br>${esc(assignReason)}</td>
        </tr>
        ${history.length ? `
          <tr>
            <td colspan="4" style="padding:0">
              <table style="width:100%; border-collapse:collapse; table-layout:fixed">
                <tr>
                  <th style="width:34%" class="center">DE</th>
                  <th style="width:34%" class="center">A</th>
                  <th style="width:16%" class="center">FECHA</th>
                  <th style="width:16%" class="center">MOTIVO</th>
                </tr>
                ${history.slice(0, 3).map((h) => `
                  <tr>
                    <td>${esc([h.from_name, h.from_dni].filter(Boolean).join(' - '))}</td>
                    <td>${esc([h.to_name, h.to_dni].filter(Boolean).join(' - '))}</td>
                    <td class="center">${esc(h.assignment_date || '')}</td>
                    <td>${esc(h.assignment_reason || '')}</td>
                  </tr>
                `).join('')}
              </table>
            </td>
          </tr>
        ` : ''}
      </table>

      <table class="section">
        <tr><th class="section-title">5. OBSERVACIONES</th></tr>
        <tr><td style="min-height:32px">${esc(generalNotes || '')}</td></tr>
      </table>

      <table class="section diag">
        <tr><th colspan="6" class="section-title">6. PROTOCOLO DE DIAGNÓSTICO</th></tr>
        <tr>
          <th style="width:14%" class="center">CATEGORÍA</th>
          <th style="width:54%" class="center">PUNTO DE VERIFICACIÓN</th>
          <th style="width:8%" class="center">BUENO</th>
          <th style="width:8%" class="center">REGULAR</th>
          <th style="width:8%" class="center">MALO</th>
          <th style="width:8%" class="center">N/A</th>
        </tr>
        ${[
          { cat: 'Computadores', rows: [
            { id: 'pc_boot', q: 'Encendido/arranque sin errores' },
            { id: 'pc_case_keyboard', q: 'Carcasa/teclado sin daños ni teclas faltantes' },
            { id: 'pc_performance', q: 'Velocidad operativa en tareas básicas' },
          ]},
          { cat: 'Monitores', rows: [
            { id: 'mon_image', q: 'Calidad de imagen sin manchas/rayas/píxeles muertos' },
            { id: 'mon_physical', q: 'Base y botones de ajuste funcionales' },
            { id: 'mon_ports', q: 'Cables/puertos de video y energía en buen estado' },
            { id: 'mon_screen', q: 'Pantalla sin fisuras; táctil funcional (si aplica)' },
          ]},
          { cat: 'Celulares', rows: [
            { id: 'ph_battery', q: 'Batería carga correctamente y mantiene autonomía' },
            { id: 'ph_connectivity', q: 'Conectividad móvil y Wi‑Fi estable' },
          ]},
          { cat: 'Impresoras', rows: [
            { id: 'pr_quality', q: 'Calidad de impresión clara, sin manchas/rayas' },
            { id: 'pr_paper', q: 'Tracción de papel sin atascos' },
            { id: 'pr_consumables', q: 'Niveles de tóner/tinta operativos' },
          ]},
        ].flatMap((c) => {
          return c.rows.map((r, idx) => {
            const v = String(diag?.[r.id] || '').trim();
            const mark = (opt) => (v === opt ? 'X' : '');
            return `
              <tr>
                ${idx === 0 ? `<td rowspan="${c.rows.length}" class="cat"><strong>${esc(c.cat)}</strong></td>` : ''}
                <td class="q">${esc(r.q)}</td>
                <td class="center">${mark('Bueno')}</td>
                <td class="center">${mark('Regular')}</td>
                <td class="center">${mark('Malo')}</td>
                <td class="center">${mark('N/A')}</td>
              </tr>
            `;
          });
        }).join('')}
      </table>

      <table class="section">
        <tr>
          <td style="width:100%; height:34px; vertical-align:bottom"><div style="height:14px; border-bottom:1px solid #000"></div><div class="center">Validado por</div></td>
        </tr>
      </table>
    </div>
    <script>
      window.onload = () => { window.__HV_READY__ = true; ${autoPrint ? 'window.focus(); window.print();' : ''} };
      ${autoPrint ? "window.onafterprint = () => { try { parent.document.body.removeChild(parent.document.body.querySelector('iframe[name=PRINT]')); } catch(e) {} };" : ''}
    </script>
  </body></html>`);
  w.document.close();

  return { frame, w, title };
}

export async function generateHvActivoPdfBlob(asset, options = {}) {
  const { frame, w, title } = await printHvActivo(asset, { ...options, autoPrint: false, renderForPdf: true });
  try {
    for (let i = 0; i < 60; i++) {
      if (w.__HV_READY__) break;
      await new Promise((r) => setTimeout(r, 50));
    }
    const imgs = Array.from(w.document.images || []);
    await Promise.all(imgs.map((img) => (
      img.complete ? Promise.resolve() : new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; })
    )));
    await new Promise((r) => setTimeout(r, 60));

    const el = w.document.querySelector('.sheet') || w.document.body;
    const renderScale = 2;
    const canvas = await html2canvas(el, {
      scale: renderScale,
      backgroundColor: '#FFFFFF',
      useCORS: true,
      windowWidth: 1200,
      windowHeight: 1700,
    });
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 6;
    const pxToMm = 0.26458333333333334;
    const mmW = (canvas.width / renderScale) * pxToMm;
    const mmH = (canvas.height / renderScale) * pxToMm;
    const maxW = pageW - (margin * 2);
    const pageInnerH = pageH - (margin * 2);
    const fitScale = Math.min(1, maxW / mmW);
    const drawW = mmW * fitScale;
    const drawH = mmH * fitScale;

    let offsetY = 0;
    pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
    while (offsetY + pageInnerH < drawH) {
      offsetY += pageInnerH;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
    }
    const blob = pdf.output('blob');
    return { blob, title };
  } finally {
    try { frame.remove(); } catch (_) {}
  }
}
